import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Snackbar,
  IconButton,
  Stack,
  LinearProgress,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";
import * as XLSX from "xlsx";
import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBinoculars,
  faFileDownload,
  faFileImport,
  faArrowRotateLeft,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import { formatDateTime } from "../../../utils/formatDate";
import { downloadExcelTemplate } from "../../../utils/exportTemplate";

const initialForm = {
  showcase_year: "",
  project_title: "",
  tech_prep_partner: "",
  tech_prep_SOD: "",
  tech_prep_pathway: "",
  competitive_category: "",
  manual_competitive_category: "",
  f_finalized_competitive_category: "",
  Instructor_ID: "",
  f_finalized_competitive_group: "",
  f_generated_competitive_group: "",
  grouping: "",
  attending_in_person: "",
  medical_bioscience_paper: "",
  video_presentation_url: "",
  project_summary_question_1: "",
  project_summary_question_2: "",
  project_summary_question_3: "",
  electricity_required: "",
  oversized_project: "",
  team_member_1: "",
  team_member_2: "",
  team_member_3: "",
  team_member_4: "",
  team_member_5: "",
  f_team_members: "",
  student_email: "",
  judge_team_number: "",
  table_number: "",
  grad_year: "",
  f_table_location: "",
  note_from_team_app_A: "",
  note_from_team_app_B: "",
  internal_note: "",
  showcase_team_password: "",
  updated_stamp: "",
  app_B_submitted: "",
  location: "",
  participation_status: "",
  showcase_ID_string: "",
  created_at: "",
};

const ShowcaseSubmissions = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    showcase_year: "",
    project_title: "",
    tech_prep_partner: "",
    tech_prep_SOD: "",
    tech_prep_pathway: "",
    competitive_category: "",
    manual_competitive_category: "",
    f_finalized_competitive_category: "",
    Instructor_ID: "",
    f_finalized_competitive_group: "",
    f_generated_competitive_group: "",
    grouping: "",
    attending_in_person: "",
    medical_bioscience_paper: "",
    video_presentation_url: "",
    project_summary_question_1: "",
    project_summary_question_2: "",
    project_summary_question_3: "",
    electricity_required: "",
    oversized_project: "",
    team_member_1: "",
    team_member_2: "",
    team_member_3: "",
    team_member_4: "",
    team_member_5: "",
    f_team_members: "",
    student_email: "",
    judge_team_number: "",
    table_number: "",
    grad_year: "",
    f_table_location: "",
    note_from_team_app_A: "",
    note_from_team_app_B: "",
    internal_note: "",
    showcase_team_password: "",
    updated_stamp: "",
    app_B_submitted: "",
    location: "",
    participation_status: "",
    showcase_ID_string: "",
    Active: "", // Default to show all
  });
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importStatus, setImportStatus] = useState({
    open: false,
    added: 0,
    updated: 0,
    total: 0,
    loading: false,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [instructorMap, setInstructorMap] = useState({});
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [activeLocations, setActiveLocations] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [participationStatusOptions, setParticipationStatusOptions] = useState(
    [],
  );

  const COLLECTION_NAME = "ShowcaseSubmissions";

  // Absolute URL to the public Tech Prep Showcase form — use PUBLIC_URL + hash route to match app routing
  // Use the same route defined in MainRoutes (/tech-prep-showcase)
  const formUrl = `${process.env.PUBLIC_URL || ""}/#/tech-prep-showcase`;

  // Format created_at/updated_stamp for grid and detail views as MM-DD-YYYY h:mm AM/PM
  const formatCreatedAtLong = (value) => formatDateTime(value);

  // Helper: convert various date/timestamp values to milliseconds for sorting
  const getTimeFromValue = (value) => {
    if (!value) return 0;
    if (value && typeof value === "object" && value.seconds)
      return value.seconds * 1000;
    if (value instanceof Date) return value.getTime();
    if (typeof value === "string") {
      // Handle MM-DD-YYYY or MM/DD/YYYY or MM-DD-YY formats
      const m = value.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
      if (m) {
        let month = Number(m[1]) - 1;
        let day = Number(m[2]);
        let year = Number(m[3]);
        if (String(m[3]).length === 2) {
          year = 2000 + year;
        }
        return new Date(year, month, day).getTime();
      }
      // Replace ' at ' used in long format before parsing
      const clean = value.includes(" at ") ? value.replace(" at ", " ") : value;
      const parsed = Date.parse(clean);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      let rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by created_at descending (most recent first)
      rows = rows.sort(
        (a, b) =>
          getTimeFromValue(b.created_at) - getTimeFromValue(a.created_at),
      );
      setData(rows);
    };

    const fetchFormStatus = async () => {
      // Form status toggle moved to TpShowcaseToggles page
    };

    const fetchInstructors = async () => {
      const snap = await getDocs(collection(db, "users"));
      const map = {};
      snap.forEach((d) => {
        const user = d.data();
        // users collection uses InstructorID; ShowcaseSubmissions uses Instructor_ID
        const instructorId = user.InstructorID;
        if (instructorId) {
          const last = user.InstLastName || "";
          const first = user.InstFirstName || "";
          let displayName = "";
          if (last && first) {
            displayName = `${last}, ${first}`;
          } else if (last || first) {
            displayName = last || first;
          } else {
            displayName = user.name || user.displayName || "";
          }
          map[instructorId] = displayName;
        }
      });
      setInstructorMap(map);
    };

    // Fetch active locations for dropdown
    const fetchLocations = async () => {
      const snap = await getDocs(collection(db, "ShowcaseLocations"));
      const locations = [];
      snap.forEach((doc) => {
        const d = doc.data();
        if (d.Active === "-1") {
          locations.push(d.Location_Name);
        }
      });
      locations.sort((a, b) => a.localeCompare(b));
      setActiveLocations(locations);
    };

    // Fetch competitive categories for dropdowns
    const fetchCategories = async () => {
      const snap = await getDocs(collection(db, "ShowcaseCategories"));
      const cats = [];
      snap.forEach((doc) => {
        const d = doc.data();
        if (d.competitive_category) cats.push(d.competitive_category);
      });
      cats.sort((a, b) => a.localeCompare(b));
      setCategoryOptions(cats);
    };

    // Fetch participation status options for dropdown
    const fetchParticipationStatus = async () => {
      const snap = await getDocs(collection(db, "ShowcaseParticipationStatus"));
      const statuses = [];
      snap.forEach((doc) => {
        const d = doc.data();
        if (d.participation_status) statuses.push(d.participation_status);
      });
      statuses.sort((a, b) => a.localeCompare(b));
      setParticipationStatusOptions(statuses);
    };

    fetchData();
    fetchFormStatus();
    fetchInstructors();
    fetchLocations();
    fetchCategories();
    fetchParticipationStatus();
  }, []);

  // Toggle handler removed — form status is now managed in TpShowcaseToggles

  useEffect(() => {
    let filtered = data.map((row) => ({
      ...row,
      Instructor_Name:
        (row.Instructor_ID && instructorMap[row.Instructor_ID]) ||
        row.Instructor_ID ||
        "",
    }));
    filtered = filtered.filter((row) =>
      Object.keys(filter).every((key) =>
        !filter[key]
          ? true
          : (row[key] || "")
              .toString()
              .toLowerCase()
              .includes(filter[key].toString().toLowerCase()),
      ),
    );
    // Sort filtered results by created_at (most recent first), then fallback to partner/year/title
    filtered = filtered.sort((a, b) => {
      // Primary: created_at descending
      const timeDiff =
        getTimeFromValue(b.created_at) - getTimeFromValue(a.created_at);
      if (timeDiff !== 0) return timeDiff;

      // Fallback: tech_prep_partner asc
      const partnerCompare = (a.tech_prep_partner || "").localeCompare(
        b.tech_prep_partner || "",
      );
      if (partnerCompare !== 0) return partnerCompare;

      // Fallback: showcase_year asc
      const yearCompare = String(a.showcase_year || "").localeCompare(
        String(b.showcase_year || ""),
      );
      if (yearCompare !== 0) return yearCompare;

      // Final fallback: project_title asc
      return (a.project_title || "").localeCompare(b.project_title || "");
    });
    setFilteredData(filtered);
  }, [data, filter, instructorMap]);

  // Compute f_team_members from team_member_1-5
  useEffect(() => {
    const members = [
      form.team_member_1,
      form.team_member_2,
      form.team_member_3,
      form.team_member_4,
      form.team_member_5,
    ]
      .map((m) => (m || "").trim())
      .filter((m) => m.length > 0)
      .join(", ");
    if (form.f_team_members !== members) {
      setForm((prev) => ({ ...prev, f_team_members: members }));
    }
    // eslint-disable-next-line
  }, [
    form.team_member_1,
    form.team_member_2,
    form.team_member_3,
    form.team_member_4,
    form.team_member_5,
  ]);

  const handleOpenAdd = () => {
    setEditIndex(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const handleOpenEdit = (params) => {
    const idx = data.findIndex((d) => d.id === params.row.id);
    // Compute formulas for finalized/competitive group fields
    const manualCat = params.row.manual_competitive_category || "";
    const compCat = params.row.competitive_category || "";
    const grouping = params.row.grouping || "";
    const f_finalized_competitive_category =
      manualCat === "" ? compCat : manualCat;
    const f_finalized_competitive_group =
      (manualCat === "" ? compCat : manualCat) + ": Group " + grouping;
    const f_generated_competitive_group = compCat + ": Group " + grouping;

    // Normalize Instructor_ID: the public form stores a display name instead of
    // the actual InstructorID key. Do a reverse lookup so the dropdown matches.
    let normalizedInstructorId = params.row.Instructor_ID || "";
    if (normalizedInstructorId && !instructorMap[normalizedInstructorId]) {
      const match = Object.entries(instructorMap).find(
        ([, name]) => name === normalizedInstructorId,
      );
      if (match) normalizedInstructorId = match[0];
    }

    // Normalize yes/no fields: public form stores "yes"/"no", legacy admin
    // records may have "-1"/"0". Unify to "yes"/"no" for the radio buttons.
    const normalizeYesNo = (val) => {
      const v = String(val ?? "")
        .toLowerCase()
        .trim();
      if (v === "-1" || v === "1" || v === "yes" || v === "true") return "yes";
      if (v === "0" || v === "no" || v === "false") return "no";
      return val ?? "";
    };

    // Normalize app_B_submitted to title-case "Yes"/"No"
    const normalizeYesNoCap = (val) => {
      const v = String(val ?? "")
        .toLowerCase()
        .trim();
      if (v === "yes" || v === "-1" || v === "1" || v === "true") return "Yes";
      if (v === "no" || v === "0" || v === "false") return "No";
      return val ?? "";
    };

    setEditIndex(idx);
    setForm({
      ...params.row,
      Instructor_ID: normalizedInstructorId,
      electricity_required: normalizeYesNo(params.row.electricity_required),
      oversized_project: normalizeYesNo(params.row.oversized_project),
      attending_in_person: normalizeYesNo(params.row.attending_in_person),
      app_B_submitted: normalizeYesNoCap(params.row.app_B_submitted),
      f_finalized_competitive_category,
      f_finalized_competitive_group,
      f_generated_competitive_group,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Only ask for confirmation when editing an existing record — a fresh
  // Add form has nothing at risk yet. The X (and backdrop/Escape, which
  // share this same handler) go through the same check.
  const handleCloseAttempt = () => {
    if (editIndex !== null) {
      setConfirmCloseOpen(true);
    } else {
      handleCloseModal();
    }
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    handleCloseModal();
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const generateUniqueId = async () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let id;
    let exists = true;
    while (exists) {
      id = Array.from(
        { length: 8 },
        () => chars[Math.floor(Math.random() * chars.length)],
      ).join("");
      // Check if this ID already exists in the collection
      const q = query(
        collection(db, COLLECTION_NAME),
        where("showcase_ID_string", "==", id),
      );
      const snapshot = await getDocs(q);
      exists = !snapshot.empty;
    }
    return id;
  };

  // Helper to format various date inputs to MM-DD-YYYY string for UI/local state
  const formatDateToMMDDYYYY = (d) => {
    if (!d) return "";
    // Firestore Timestamp
    if (typeof d === "object" && d.seconds) {
      const date = new Date(d.seconds * 1000);
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const yyyy = date.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    }
    // Already a formatted MM-DD-YYYY string
    if (typeof d === "string" && d.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
      return d;
    }
    // JS Date or other string parseable by Date
    const dateObj = d instanceof Date ? d : new Date(d);
    if (!dateObj || isNaN(dateObj.getTime())) return "";
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");
    const yyyy = dateObj.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const handleSave = async () => {
    if (!form.showcase_year || !form.project_title) {
      setSnackbar({
        open: true,
        message: "Showcase Year and Project Title are required.",
        severity: "error",
      });
      return;
    }
    try {
      if (editIndex !== null) {
        const docId = data[editIndex].id;
        // Set updated_stamp to now (store Date in Firestore) but keep a formatted string in local state
        const now = new Date();
        const updatedFormForDb = { ...form, updated_stamp: now };
        await setDoc(doc(db, COLLECTION_NAME, docId), updatedFormForDb);
        const newData = [...data];
        // Keep UI/local state dates as MM-DD-YYYY strings to avoid rendering Date objects
        newData[editIndex] = {
          ...updatedFormForDb,
          id: docId,
          updated_stamp: formatDateToMMDDYYYY(now),
        };
        setData(newData);
        setSnackbar({
          open: true,
          message: "Showcase submission updated",
          severity: "success",
        });
      } else {
        // Set created_at and updated_stamp to now, generate unique showcase_ID_string
        const uniqueId = await generateUniqueId();
        const now = new Date();
        const newFormForDb = {
          ...form,
          created_at: now,
          updated_stamp: now,
          showcase_ID_string: uniqueId,
        };
        const docRef = await addDoc(
          collection(db, COLLECTION_NAME),
          newFormForDb,
        );
        // Append new record at top to match initial fetch sort by created_at desc
        // Store formatted date strings in local state to avoid rendering Date objects
        const newLocal = {
          ...newFormForDb,
          id: docRef.id,
          // Keep created_at as Date so grid can show full timestamp; updated_stamp keep as Date/string handled elsewhere
          created_at: now,
          updated_stamp: formatDateToMMDDYYYY(now),
        };
        setData([newLocal, ...data]);
        setSnackbar({
          open: true,
          message: "Showcase submission added",
          severity: "success",
        });
      }
      setModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving to Firestore",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (editIndex !== null) {
      const docId = data[editIndex].id;
      await deleteDoc(doc(db, COLLECTION_NAME, docId));
      const newData = data.filter((_, idx) => idx !== editIndex);
      setData(newData);
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "Showcase submission deleted",
        severity: "success",
      });
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({
      open: true,
      added: 0,
      updated: 0,
      total: 0,
      loading: true,
    });
    const reader = new FileReader();

    const processExcelRow = async (row, merged, counts) => {
      const { added, updated } = counts;
      const idx = merged.findIndex(
        (d) =>
          d.showcase_year === row.showcase_year &&
          d.project_title === row.project_title,
      );
      if (idx > -1) {
        const docId = merged[idx].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), row);
        merged[idx] = { ...merged[idx], ...row };
        counts.updated = updated + 1;
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), row);
        merged.push({ ...row, id: docRef.id });
        counts.added = added + 1;
      }
    };

    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws);

      const merged = [...data];
      const counts = { added: 0, updated: 0 };

      for (const row of jsonData) {
        await processExcelRow(row, merged, counts);
        setImportStatus((s) => ({
          ...s,
          total: jsonData.length,
          added: counts.added,
          updated: counts.updated,
        }));
      }

      setData(merged);
      setSnackbar({
        open: true,
        message: "Excel data imported",
        severity: "success",
      });
      setImportStatus((s) => ({ ...s, loading: false }));
    };

    reader.readAsBinaryString(file);
  };

  const handleDeleteMultipleConfirmed = async () => {
    for (const id of rowSelectionModel) {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
    }
    setData(data.filter((row) => !rowSelectionModel.includes(row.id)));
    setRowSelectionModel([]);
    setConfirmDeleteOpen(false);
    setSnackbar({
      open: true,
      message: "Selected showcase submissions deleted",
      severity: "success",
    });
  };

  const handleOpenConfirmDelete = () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  const handleOpenDetails = (params) => {
    setDetailRow(params.row);
    setDetailDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
    setDetailRow(null);
  };

  // Export actual data to Excel
  const handleExportExcel = () => {
    if (!filteredData.length) {
      setSnackbar({
        open: true,
        message: "No data to export",
        severity: "warning",
      });
      return;
    }
    const exportData = filteredData.map(({ id, ...row }) => row); // Remove id field
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ShowcaseSubmissions");
    XLSX.writeFile(workbook, "showcase-submissions.xlsx");
  };

  const handleExportTemplate = () => {
    downloadExcelTemplate(
      Object.keys(initialForm),
      `${COLLECTION_NAME}-template`,
    );
  };

  const rowsForGrid = filteredData.map((row) => ({
    ...row,
    id: String(row.id),
  }));

  const columns = [
    {
      field: "tech_prep_partner",
      headerName: "Tech Prep Partner",
      flex: 1,
      sortable: true,
    },
    {
      field: "tech_prep_SOD",
      headerName: "Tech Prep SOD",
      flex: 1,
      sortable: true,
    },
    {
      field: "competitive_category",
      headerName: "Competitive Category",
      flex: 1,
      sortable: true,
    },
    {
      field: "project_title",
      headerName: "Project Title",
      flex: 1.5,
      sortable: true,
    },
    {
      field: "Instructor_Name",
      headerName: "Instructor",
      flex: 1,
      sortable: true,
    },
    {
      field: "location",
      headerName: "Table Location",
      flex: 1,
      sortable: true,
    },
    {
      field: "judge_team_number",
      headerName: "Judge Team #",
      flex: 0.7,
      sortable: true,
    },
    {
      field: "attending_in_person",
      headerName: "Attending In Person",
      flex: 0.8,
      sortable: true,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      sortable: true,
      renderCell: (params) => formatCreatedAtLong(params.value),
      sortComparator: (v1, v2) => getTimeFromValue(v1) - getTimeFromValue(v2),
    },
    {
      field: "updated_stamp",
      headerName: "Updated At",
      flex: 1,
      sortable: true,
      renderCell: (params) => formatCreatedAtLong(params.value),
      sortComparator: (v1, v2) => getTimeFromValue(v1) - getTimeFromValue(v2),
    },
  ];

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Box
          component="img"
          src="/img/krakenblack.png"
          alt="Kraken Logo"
          sx={{ height: 36, width: "auto" }}
        />
        <Typography variant="h4" gutterBottom>
          Showcase Submissions
        </Typography>
      </Box>
      <TechPrepPortalMgtBreadCrumbs pageName="Showcase Submissions" />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <TextField
          label="Showcase Year"
          name="showcase_year"
          value={filter.showcase_year}
          onChange={(e) =>
            setFilter({ ...filter, showcase_year: e.target.value })
          }
          variant="outlined"
          size="small"
          fullWidth
          sx={{ minWidth: 120 }}
        />
        <TextField
          label="Project Title"
          name="project_title"
          value={filter.project_title}
          onChange={(e) =>
            setFilter({ ...filter, project_title: e.target.value })
          }
          variant="outlined"
          size="small"
          fullWidth
        />
        <TextField
          label="Tech Prep Partner"
          name="tech_prep_partner"
          value={filter.tech_prep_partner}
          onChange={(e) =>
            setFilter({ ...filter, tech_prep_partner: e.target.value })
          }
          variant="outlined"
          size="small"
          fullWidth
        />
        <TextField
          label="Instructor"
          name="Instructor_ID"
          value={filter.Instructor_ID}
          onChange={(e) =>
            setFilter({ ...filter, Instructor_ID: e.target.value })
          }
          variant="outlined"
          size="small"
          fullWidth
        />
        <IconButton component="label" size="medium">
          <FontAwesomeIcon icon={faFileImport} color="#2e7d32" />
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleExcelUpload}
          />
        </IconButton>
        {/* Only one export icon, blue, for exporting actual data */}
        <IconButton onClick={handleExportExcel} size="medium">
          <FontAwesomeIcon icon={faFileDownload} color="#1976d2" />
        </IconButton>
        <Button
          variant="contained"
          color="success"
          startIcon={<FontAwesomeIcon icon={faFileExcel} />}
          sx={{ color: "white" }}
          onClick={handleExportTemplate}
        >
          Export Template
        </Button>
        <IconButton
          color="primary"
          onClick={handleOpenAdd}
          sx={{
            height: 40,
            width: 40,
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
          }}
          aria-label="Add Submission"
        >
          <AddIcon sx={{ color: "#fff" }} />
        </IconButton>
        <ClearCollectionButton collectionName="ShowcaseSubmissions" />
      </Stack>
      <div style={{ height: 720, width: "100%" }}>
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          pageSize={100}
          rowsPerPageOptions={[100, 200, 500]}
          onRowClick={handleOpenEdit}
          components={{
            Toolbar: () => (
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleOpenConfirmDelete}
                    disabled={rowSelectionModel.length === 0}
                    startIcon={<DeleteIcon />}
                  >
                    Delete Selected
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const element = document.getElementById("excel-upload");
                      if (element) element.click();
                    }}
                    startIcon={<UploadFileIcon />}
                  >
                    Import from Excel
                  </Button>
                  <input
                    id="excel-upload"
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={handleExcelUpload}
                    style={{ display: "none" }}
                  />
                  <IconButton component="label" size="medium">
                    <FontAwesomeIcon icon={faFileImport} color="#2e7d32" />
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      hidden
                      onChange={handleExcelUpload}
                    />
                  </IconButton>
                  {/* Only one export icon, blue, for exporting actual data */}
                  <IconButton onClick={handleExportExcel} size="medium">
                    <FontAwesomeIcon icon={faFileDownload} color="#1976d2" />
                  </IconButton>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                    sx={{ color: "white" }}
                    onClick={handleExportTemplate}
                  >
                    Export Template
                  </Button>
                </Grid>
              </Grid>
            ),
          }}
        />
      </div>
      <Dialog
        open={modalOpen}
        onClose={handleCloseAttempt}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <ModalDialogTitle onClose={handleCloseAttempt}>
          {editIndex !== null ? "Edit" : "Add"} Showcase Submission
        </ModalDialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            {/* Removed Form Status toggle — kept only in top filter bar */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Showcase ID
              </Box>
              <TextField
                value={form.showcase_ID_string}
                variant="outlined"
                size="small"
                fullWidth
                disabled
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Showcase Year
              </Box>
              <TextField
                name="showcase_year"
                value={form.showcase_year}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Showcase Year"
                sx={{ mb: 2 }}
                type="number"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Project Title
              </Box>
              <TextField
                name="project_title"
                value={form.project_title}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Project Title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Tech Prep Partner
              </Box>
              <TextField
                name="tech_prep_partner"
                value={form.tech_prep_partner}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Tech Prep Partner"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Tech Prep SOD
              </Box>
              <TextField
                name="tech_prep_SOD"
                value={form.tech_prep_SOD}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Tech Prep SOD"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Tech Prep Pathway
              </Box>
              <TextField
                name="tech_prep_pathway"
                value={form.tech_prep_pathway}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Tech Prep Pathway"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Competitive Category
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  name="competitive_category"
                  value={form.competitive_category}
                  onChange={handleFormChange}
                  variant="outlined"
                  size="small"
                  displayEmpty
                  inputProps={{ "aria-label": "Competitive Category" }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {categoryOptions.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Manual Competitive Category
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  name="manual_competitive_category"
                  value={form.manual_competitive_category}
                  onChange={handleFormChange}
                  variant="outlined"
                  size="small"
                  displayEmpty
                  inputProps={{ "aria-label": "Manual Competitive Category" }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {categoryOptions.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Finalized Competitive Category
              </Box>
              <TextField
                name="f_finalized_competitive_category"
                value={form.f_finalized_competitive_category}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Finalized Competitive Category"
                sx={{ mb: 2 }}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Finalized Competitive Group
              </Box>
              <TextField
                name="f_finalized_competitive_group"
                value={form.f_finalized_competitive_group}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Finalized Competitive Group"
                sx={{ mb: 2 }}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Generated Competitive Group
              </Box>
              <TextField
                name="f_generated_competitive_group"
                value={form.f_generated_competitive_group}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Generated Competitive Group"
                sx={{ mb: 2 }}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Grouping
              </Box>
              <TextField
                name="grouping"
                value={form.grouping}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Grouping"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Instructor
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  name="Instructor_ID"
                  value={form.Instructor_ID}
                  onChange={handleFormChange}
                  variant="outlined"
                  size="small"
                  displayEmpty
                  inputProps={{ "aria-label": "Instructor" }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {Object.entries(instructorMap)
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([id, name]) => (
                      <MenuItem key={id} value={id}>
                        {name} ({id})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Student Email
              </Box>
              <TextField
                name="student_email"
                value={form.student_email}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Student Email"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Judge Team Number
              </Box>
              <TextField
                name="judge_team_number"
                value={form.judge_team_number}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Judge Team Number"
                sx={{ mb: 2 }}
                type="number"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Table Number
              </Box>
              <TextField
                name="table_number"
                value={form.table_number}
                onChange={handleFormChange}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: false }}
                placeholder="Table Number"
                sx={{ mb: 2 }}
                type="number"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
            </Grid>

            {/* Location field from Firestore (Select using activeLocations) */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Location
              </Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  name="location"
                  value={form.location}
                  onChange={handleFormChange}
                  variant="outlined"
                  size="small"
                  displayEmpty
                  inputProps={{ "aria-label": "Location" }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {activeLocations.map((loc) => (
                    <MenuItem key={loc} value={loc}>
                      {loc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Video Presentation URL — sits next to Location; link icon opens URL in new tab */}
            <Grid item xs={12} md={8}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Video Presentation URL
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <TextField
                  name="video_presentation_url"
                  value={form.video_presentation_url}
                  onChange={handleFormChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="https://..."
                />
                <IconButton
                  size="small"
                  disabled={!form.video_presentation_url}
                  onClick={() =>
                    window.open(
                      form.video_presentation_url,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  sx={{
                    color: form.video_presentation_url
                      ? "#1976d2"
                      : "text.disabled",
                    flexShrink: 0,
                  }}
                  aria-label="Open video URL in new tab"
                >
                  <FontAwesomeIcon icon={faLink} size="sm" />
                </IconButton>
              </Box>
            </Grid>

            {/* Medical Bioscience Paper — sits next to Video Presentation URL; button opens the uploaded document */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Medical Bioscience Paper
              </Box>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!form.medical_bioscience_paper}
                  onClick={() =>
                    window.open(
                      form.medical_bioscience_paper,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  startIcon={<FontAwesomeIcon icon={faLink} size="sm" />}
                >
                  {form.medical_bioscience_paper
                    ? "View Document"
                    : "No Document Uploaded"}
                </Button>
              </Box>
            </Grid>

            {/* All four radio groups together in one row */}
            <Grid item xs={12} md={3}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Attending In Person
              </Box>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  name="attending_in_person"
                  value={form.attending_in_person}
                  onChange={handleFormChange}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                App B Submitted
              </Box>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  name="app_B_submitted"
                  value={form.app_B_submitted}
                  onChange={handleFormChange}
                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Electricity Required
              </Box>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  name="electricity_required"
                  value={form.electricity_required}
                  onChange={(e) =>
                    setForm({ ...form, electricity_required: e.target.value })
                  }
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Oversized Project
              </Box>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  name="oversized_project"
                  value={form.oversized_project}
                  onChange={(e) =>
                    setForm({ ...form, oversized_project: e.target.value })
                  }
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {/* Created At and Updated At displayed left-to-right at bottom (read-only) */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Created At
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {formatCreatedAtLong(form.created_at) || ""}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{ mb: 1, fontWeight: 600, color: "#1976d2", fontSize: 15 }}
              >
                Updated At
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {formatCreatedAtLong(form.updated_stamp) || ""}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSave}
          onDelete={
            editIndex !== null ? () => setConfirmDeleteOpen(true) : undefined
          }
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={async () => {
          await handleDelete();
          setConfirmDeleteOpen(false);
        }}
        message="Are you sure you want to delete this submission? This action cannot be undone."
      />
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseDetails}>
          Showcase Submission Details
        </ModalDialogTitle>
        <DialogContent>
          {detailRow && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {detailRow.project_title}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Showcase Year:</strong> {detailRow.showcase_year}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Tech Prep Partner:</strong>{" "}
                {detailRow.tech_prep_partner}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Tech Prep SOD:</strong> {detailRow.tech_prep_SOD}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Tech Prep Pathway:</strong>{" "}
                {detailRow.tech_prep_pathway}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Competitive Category:</strong>{" "}
                {detailRow.competitive_category}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Manual Competitive Category:</strong>{" "}
                {detailRow.manual_competitive_category}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Finalized Competitive Category:</strong>{" "}
                {detailRow.f_finalized_competitive_category}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Instructor ID:</strong> {detailRow.Instructor_ID}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Finalized Competitive Group:</strong>{" "}
                {detailRow.f_finalized_competitive_group}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Generated Competitive Group:</strong>{" "}
                {detailRow.f_generated_competitive_group}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Grouping:</strong> {detailRow.grouping}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Attending In Person:</strong>{" "}
                {detailRow.attending_in_person}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Medical Bioscience Paper:</strong>{" "}
                {detailRow.medical_bioscience_paper}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Video Presentation URL:</strong>{" "}
                {detailRow.video_presentation_url}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Project Summary Question 1:</strong>{" "}
                {detailRow.project_summary_question_1}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Project Summary Question 2:</strong>{" "}
                {detailRow.project_summary_question_2}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Project Summary Question 3:</strong>{" "}
                {detailRow.project_summary_question_3}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Electricity Required:</strong>{" "}
                {detailRow.electricity_required}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Oversized Project:</strong>{" "}
                {detailRow.oversized_project}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Team Members:</strong> {detailRow.f_team_members}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Student Email:</strong> {detailRow.student_email}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Judge Team Number:</strong>{" "}
                {detailRow.judge_team_number}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Table Number:</strong> {detailRow.table_number}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Table Location:</strong> {detailRow.f_table_location}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Note from Team App A:</strong>{" "}
                {detailRow.note_from_team_app_A}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Note from Team App B:</strong>{" "}
                {detailRow.note_from_team_app_B}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Internal Note:</strong> {detailRow.internal_note}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Showcase Team Password:</strong>{" "}
                {detailRow.showcase_team_password}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>App B Submitted:</strong> {detailRow.app_B_submitted}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Location:</strong> {detailRow.location}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Participation Status:</strong>{" "}
                {detailRow.participation_status}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Showcase ID String:</strong>{" "}
                {detailRow.showcase_ID_string}
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  navigator.clipboard.writeText(
                    detailRow.showcase_team_password,
                  );
                  setSnackbar({
                    open: true,
                    message: "Password copied to clipboard",
                    severity: "success",
                  });
                }}
                sx={{ mt: 2 }}
              >
                Copy Password
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {importStatus.loading && (
        <LinearProgress
          variant="determinate"
          value={(importStatus.added / importStatus.total) * 100}
          sx={{ position: "absolute", top: 64, left: 0, right: 0 }}
        />
      )}
    </Box>
  );
};

export default ShowcaseSubmissions;
